import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react';
import SEO from '@/components/SEO';
import HeroSection from '@/components/HeroSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WHALE_IMAGE = 'https://customer-assets.emergentagent.com/job_navs-art-studio/artifacts/j2uwmz8f_68BB0444-5D6B-4E65-94A6-BFF19BB84356.JPG';

const CURATED_IDS = [
  '4252fc80-2e6f-4288-86d9-c14a07eb0e80',
  'b96eef2e-79c3-4216-a414-001b412fb876',
  '787aef20-0bec-4df6-9a77-7a1187450b79',
  '5f540675-0ae5-4889-b852-353cc600b975',
  '3f57ba08-17fd-4d6b-a2a3-95a0ad92cd7b',
  '6b0afa50-a295-466c-b222-299586382bc6',
  'e912d4b2-f107-42c8-8a58-1a21ab7f6939',
  '5096cd9e-d52c-453e-b52d-23f441e6b549',
];

const Home = () => {
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchArtworks();
  }, []);

  const fetchArtworks = async () => {
    try {
      const responses = await Promise.all(
        CURATED_IDS.map(id =>
          fetch(`${API}/products/${id}`).then(r => r.ok ? r.json() : null)
        )
      );
      setArtworks(responses.filter(Boolean));
    } catch (err) {
      console.error('Failed to fetch artworks:', err);
    } finally {
      setLoading(false);
    }
  };

  const next = useCallback(() => {
    if (artworks.length === 0) return;
    setCurrent(c => (c + 1) % artworks.length);
  }, [artworks.length]);

  const prev = useCallback(() => {
    if (artworks.length === 0) return;
    setCurrent(c => (c - 1 + artworks.length) % artworks.length);
  }, [artworks.length]);

  // Auto-advance slideshow every 4s
  useEffect(() => {
    if (paused || artworks.length === 0) return;
    const timer = setInterval(next, 4000);
    return () => clearInterval(timer);
  }, [paused, next, artworks.length]);

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: (i = 0) => ({
      opacity: 1, y: 0,
      transition: { duration: 0.7, delay: i * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }
    })
  };

  const collections = [
    { name: 'Ocean Series', path: '/ocean' },
    { name: 'Sports & Cars', path: '/sports-cars' },
    { name: 'Coasters', path: '/coasters' },
    { name: 'Featured', path: '/featured' },
  ];

  const currentArt = artworks[current];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAFAFA' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A1A1A]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAFAFA' }}>
      <SEO
        title="Where Texture Meets Emotion"
        description="Handcrafted, one-of-a-kind resin and textured wall art by Navs Art Studio. Ocean-inspired pieces, automotive art, coasters, and bespoke commissions delivered with elegance."
        path="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Navs Art Studio',
          url: 'https://www.navsartstudio.com',
          logo: 'https://customer-assets.emergentagent.com/job_navs-art-studio/artifacts/j2uwmz8f_68BB0444-5D6B-4E65-94A6-BFF19BB84356.JPG',
          description:
            'Handcrafted, one-of-a-kind resin and textured artwork — ocean series, automotive wall art, coasters and custom commissions.',
          sameAs: ['https://www.instagram.com/navs_artstudio'],
          contactPoint: {
            '@type': 'ContactPoint',
            email: 'navsartstudio@gmail.com',
            contactType: 'Customer Service',
          },
        }}
      />

      {/* ── Hero Section ── */}
      <HeroSection />

      {/* ── Artwork Slideshow ── */}
      {artworks.length > 0 && (
        <section
          className="py-20 lg:py-28 px-6 md:px-12 lg:px-24"
          data-testid="slideshow-section"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-14 flex items-end justify-between">
              <div>
                <p className="text-[#888] text-xs uppercase tracking-[0.3em] mb-3">Hand-Picked</p>
                <h2 className="text-4xl sm:text-5xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Curated for You
                </h2>
              </div>
              <div className="hidden sm:flex items-center gap-3">
                <button
                  onClick={prev}
                  data-testid="slideshow-prev"
                  className="w-12 h-12 rounded-full border border-black/10 flex items-center justify-center hover:bg-[#1A1A1A] hover:text-white transition-all duration-300"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={next}
                  data-testid="slideshow-next"
                  className="w-12 h-12 rounded-full border border-black/10 flex items-center justify-center hover:bg-[#1A1A1A] hover:text-white transition-all duration-300"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </motion.div>

            {/* Slideshow Main */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">

              {/* Large Image */}
              <div className="lg:col-span-7 relative overflow-hidden bg-[#E8E5E0] aspect-[4/5] lg:aspect-[3/4]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={current}
                    initial={{ opacity: 0, scale: 1.02 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.5, ease: 'easeInOut' }}
                    className="absolute inset-0"
                  >
                    <Link to={`/product/${currentArt?.id}`} className="block w-full h-full group" data-testid="slideshow-main-image">
                      <img
                        src={currentArt?.image_url}
                        alt={currentArt?.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      {currentArt?.badge && (
                        <div className="absolute top-5 left-5">
                          <span className="bg-white/90 backdrop-blur-sm px-3 py-1.5 text-[10px] uppercase tracking-widest">
                            {currentArt.badge.replace('-', ' ')}
                          </span>
                        </div>
                      )}
                    </Link>
                  </motion.div>
                </AnimatePresence>

                {/* Mobile arrows */}
                <div className="sm:hidden absolute bottom-4 right-4 flex gap-2 z-10">
                  <button onClick={prev} className="w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center">
                    <ChevronLeft size={18} />
                  </button>
                  <button onClick={next} className="w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center">
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>

              {/* Details Panel */}
              <div className="lg:col-span-5 flex flex-col justify-between py-4 lg:py-8">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={current}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                  >
                    <p className="text-[#888] text-xs uppercase tracking-[0.3em] mb-4 capitalize">
                      {currentArt?.category?.replace('-', ' ')}
                    </p>
                    <h3
                      className="text-3xl sm:text-4xl lg:text-5xl mb-6 leading-[1.15]"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                      data-testid="slideshow-title"
                    >
                      {currentArt?.name}
                    </h3>
                    <p className="text-[#4A4A4A] text-base leading-relaxed mb-8 line-clamp-3">
                      {currentArt?.description}
                    </p>
                    <p className="text-3xl font-light mb-10" data-testid="slideshow-price">
                      ${currentArt?.price?.toFixed(0)}
                    </p>
                    <Link to={`/product/${currentArt?.id}`} data-testid="slideshow-view-btn">
                      <button className="bg-[#1A1A1A] text-white hover:bg-[#333] rounded-full px-8 py-4 text-sm uppercase tracking-widest transition-all duration-300 inline-flex items-center gap-3 group">
                        View Artwork
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                      </button>
                    </Link>
                  </motion.div>
                </AnimatePresence>

                {/* Thumbnail Strip */}
                <div className="mt-10 lg:mt-auto pt-8 border-t border-black/5">
                  <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                    {artworks.map((art, i) => (
                      <button
                        key={art.id}
                        onClick={() => setCurrent(i)}
                        data-testid={`slideshow-thumb-${i}`}
                        className={`flex-shrink-0 w-16 h-16 overflow-hidden transition-all duration-300 ${
                          i === current
                            ? 'ring-2 ring-[#1A1A1A] ring-offset-2 opacity-100'
                            : 'opacity-40 hover:opacity-70'
                        }`}
                      >
                        <img src={art.image_url} alt={art.name} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Dots */}
            <div className="flex justify-center gap-2 mt-10">
              {artworks.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  data-testid={`slideshow-dot-${i}`}
                  className={`h-1 rounded-full transition-all duration-500 ${
                    i === current ? 'w-8 bg-[#1A1A1A]' : 'w-4 bg-black/15 hover:bg-black/30'
                  }`}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Browse Collections ── */}
      <section className="py-20 lg:py-28 px-6 md:px-12 lg:px-24 bg-[#F0EDEA]" data-testid="collections-section">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-14">
            <p className="text-[#888] text-xs uppercase tracking-[0.3em] mb-3">Browse</p>
            <h2 className="text-4xl sm:text-5xl" style={{ fontFamily: 'Playfair Display, serif' }}>
              Our Collections
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {collections.map((col, i) => (
              <motion.div key={col.name} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp}>
                <Link
                  to={col.path}
                  className="group block bg-white p-8 hover:bg-[#1A1A1A] transition-all duration-500 h-full"
                  data-testid={`collection-link-${col.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-end justify-between h-full min-h-[120px]">
                    <h3
                      className="text-xl lg:text-2xl group-hover:text-white transition-colors duration-500"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      {col.name}
                    </h3>
                    <ArrowUpRight size={20} className="text-[#888] group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-500" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Section ── */}
      <section className="py-24 lg:py-32 px-6 md:px-12 lg:px-24" style={{ backgroundColor: '#1A1A1A' }} data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="text-white/40 text-xs uppercase tracking-[0.3em] mb-6">Every Piece Tells a Story</p>
            <h2 className="text-4xl sm:text-5xl text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Transform Your Space
            </h2>
            <p className="text-white/50 text-base mb-12 leading-relaxed max-w-xl mx-auto">
              Discover handcrafted artwork that reflects your style and brings warmth, texture, and character to your home.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/featured" data-testid="cta-featured-btn">
                <button className="bg-white text-[#1A1A1A] hover:bg-white/90 rounded-full px-8 py-4 text-sm uppercase tracking-widest transition-all duration-300">
                  Explore Collection
                </button>
              </Link>
              <Link to="/contact" data-testid="cta-contact-btn">
                <button className="border border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-4 text-sm uppercase tracking-widest transition-all duration-300">
                  Get in Touch
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
